import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { routes } from '@platform/core/lib/routes';
import type { IRecord } from '@platform/core/types';
import type { IVizProps } from './IVizProps';
import { useVizLabels } from './useVizLabels';
import { useVizRecords } from './useVizRecords';
import { VIZ_FIELDS } from './vizFields';
import { WEIGHT_TONE } from './weightTone';

/**
 * Діаграма fishbone (Ісікави) запису-аналізу (прототип v4 vizFish): «Причин: N · з високою вагою: M»; перша половина категорій над
 * «хребтом», друга — під ним; у категорії — чипи причин у тоні ваги (підказка — вага й підпричини, клік відкриває причину); праворуч
 * стрілка до рамки «ПРОБЛЕМА» з полем «Проблема або предмет» (інакше назва аналізу).
 */
export function FishboneViz({ ws, module, rt, record }: IVizProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const field = VIZ_FIELDS.fishbone;
  const { source, items } = useVizRecords(ws, module, rt.key, record, field, true);
  const label = useVizLabels(source?.rt);
  const cats = (source?.rt.fields.find((f) => f.key === field)?.options ?? []).map((o) => o.value);
  const half = Math.ceil(cats.length / 2);
  const open = (r: IRecord) => source && navigate(routes.record(module.module.key, source.rt.key, r.id));
  const column = (cat: string) => (
    <div key={cat} className="viz-fb__cat">
      <span className="viz-fb__label">{label.option(field, cat)}</span>
      {items.filter((r) => r.values[field] === cat).map((r) => (
        <button key={r.id} type="button" className={`fmx-chip fmx-tone-${WEIGHT_TONE[String(r.values['weight'])] ?? 'neutral'}`} onClick={() => open(r)}
          title={[label.option('weight', r.values['weight']), r.values['subcauses']].filter(Boolean).join(' · ')}>{r.title || r.display}</button>
      ))}
    </div>
  );
  return (
    <div className="viz viz-fb">
      <span className="fmx-sub">{t('recordPage.viz.causes', { n: items.length, high: items.filter((r) => r.values['weight'] === 'High').length })}</span>
      <div className="viz-fb__scroll">
        <div className="viz-fb__grid">
          <div className="viz-fb__bones">
            <div className="viz-fb__row">{cats.slice(0, half).map(column)}</div>
            <div className="viz-fb__spine" aria-hidden><i /></div>
            <div className="viz-fb__row">{cats.slice(half).map(column)}</div>
          </div>
          <div className="viz-fb__arrow" aria-hidden><i /><b /></div>
          <div className="viz-fb__head">
            <span className="viz-fb__label">{t('recordPage.viz.problem')}</span>
            <strong>{String(record.values['effect'] || record.title)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
